(() => {
  'use strict';
  if (customElements.get('fab-pearl-workshop')) return;
  const positive = value => value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0;
  const validMoney = value => value != null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;
  const optionIndex = (product, name) => (product?.options || []).findIndex(value => String(value).toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr'));
  function packageGrams(variant) {
    const measure = variant?.measurement;
    if (positive(measure?.quantity_value)) {
      if (measure.quantity_unit === 'kg') return Number(measure.quantity_value) * 1000;
      if (measure.quantity_unit === 'g') return Number(measure.quantity_value);
    }
    for (const value of variant?.options || []) {
      const match = String(value).trim().match(/^(\d+(?:[.,]\d+)?)\s*(kg|g)$/i);
      if (match) return Number(match[1].replace(',','.')) * (match[2].toLowerCase() === 'kg' ? 1000 : 1);
    }
    return positive(variant?.weightG) ? Number(variant.weightG) : 0;
  }
  function unitMinor(variant) {
    const grams = packageGrams(variant);
    if (grams > 0 && validMoney(variant?.priceMinor)) return Math.round(Number(variant.priceMinor) * 1000 / grams);
    const measure = variant?.measurement;
    if (validMoney(variant?.unitPriceMinor) && positive(measure?.reference_value)) {
      if (measure.reference_unit === 'kg') return Math.round(Number(variant.unitPriceMinor) / Number(measure.reference_value));
      if (measure.reference_unit === 'g') return Math.round(Number(variant.unitPriceMinor) * 1000 / Number(measure.reference_value));
    }
    return null;
  }
  function order(variant, needed, amount) {
    if (!variant || variant.id == null || variant.id === '' || amount <= 0 || !validMoney(variant.priceMinor)) return {error:'invalid_weight'};
    if (!(needed > 0 && Number.isFinite(needed))) return {error:'invalid_quantity'};
    if (variant.available !== true) return {error:'unavailable'};
    const min = positive(variant.min) ? Math.ceil(Number(variant.min)) : 1;
    const step = positive(variant.step) ? Math.ceil(Number(variant.step)) : 1;
    const quantity = min + Math.max(0, Math.ceil((Math.ceil(needed / amount) - min) / step)) * step;
    const max = positive(variant.max) ? Number(variant.max) : Infinity;
    const stock = variant.stock == null ? Infinity : Math.max(0, Number(variant.stock));
    if (Number.isNaN(stock) || !Number.isSafeInteger(quantity) || quantity > Math.min(max, stock)) return {error:'stock_limit'};
    return {id:String(variant.id),quantity,needed,purchased:amount * quantity,leftover:amount * quantity - needed,costMinor:Number(variant.priceMinor) * quantity};
  }
  function purchase(variant,needG) {
    const result=order(variant,needG,packageGrams(variant));
    return result.error ? result : {...result,needG,purchasedG:result.purchased,leftoverG:result.leftover};
  }
  function packCount(variant,product) {
    for(const value of [...(variant?.options || []),variant?.title,product?.title]) {
      const match=String(value || '').match(/\b(\d+)\s*adet\b/i);
      if(match) return Number(match[1]);
    }
    return 0;
  }
  function plan({count, gramsPerCandle, variant, gelEnabled=false, gelVariant, gelGramsPerCandle, scented=true, wicksPerCandle=1, wickMode='tabbed', buyWicks=false, wickVariant, wickProduct}) {
    if (!(Number.isInteger(count) && count >= 1 && count <= 10000 && gramsPerCandle >= 1 && gramsPerCandle <= 100000)) return {error:'invalid_quantity'};
    const pearl = purchase(variant, count * gramsPerCandle);
    if (pearl.error) return {error:pearl.error};
    if(!(Number.isInteger(wicksPerCandle) && wicksPerCandle>=1 && wicksPerCandle<=20)) return {error:'invalid_quantity'};
    const requiredWicks=count * wicksPerCandle;
    const giftWicks=packageGrams(variant)===1000 ? pearl.quantity * (scented ? 30 : 10) : 0;
    let wick={mode:wickMode,required:requiredWicks,gift:giftWicks,ownNeeded:wickMode==='supplied'?Math.max(0,requiredWicks-giftWicks):requiredWicks,costMinor:0};
    if(wickMode==='tabbed' && buyWicks) {
      const pieces=packCount(wickVariant,wickProduct);
      if(!(pieces>0))return {error:'wick_pack_error'};
      const purchased=order(wickVariant,requiredWicks,pieces);
      if(purchased.error)return {error:purchased.error};
      wick={...wick,...purchased,ownNeeded:0,packCount:pieces};
    }
    let gel = null;
    if (gelEnabled) {
      if (!(gelGramsPerCandle >= 1 && gelGramsPerCandle <= 100000)) return {error:'invalid_quantity'};
      gel = purchase(gelVariant, count * gelGramsPerCandle);
      if (gel.error) return {error:gel.error};
    }
    return {type:'pearl',pearl,gel,wick,totalMinor:pearl.costMinor + (gel?.costMinor || 0) + wick.costMinor,items:[pearl,gel,wick.id?wick:null].filter(Boolean).map(row=>({id:row.id,quantity:row.quantity}))};
  }
  window.FabrikonePearlPlanner = Object.freeze({packageGrams,unitMinor,purchase,packCount,plan,optionIndex});
  customElements.define('fab-pearl-workshop', class extends HTMLElement {
    connectedCallback() {
      if (this.mounted) return;
      this.mounted = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.query = selector => this.querySelector(selector);
      try {
        this.catalog = JSON.parse(this.query('[data-fpw-catalog]').textContent);
        this.copy = JSON.parse(this.query('[data-fpw-i18n]').textContent);
      } catch (_) { return; }
      this.products = (this.catalog.products || []).filter(product => product.variants?.length);
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'tr', {style:'currency',currency:this.dataset.currency || 'TRY'});
      this.number = new Intl.NumberFormat(document.documentElement.lang || 'tr', {maximumFractionDigits:2});
      this.productSelect = this.query('[data-fpw-product]');
      this.weightSelect = this.query('[data-fpw-weight]');
      this.aromaSelect = this.query('[data-fpw-aroma]');
      this.gelSelect = this.query('[data-fpw-gel-variant]');
      this.wickProductSelect = this.query('[data-fpw-wick-product]');
      this.wickVariantSelect = this.query('[data-fpw-wick-variant]');
      this.status = this.query('[data-fpw-status]');
      this.addButton = this.query('[data-fpw-add]');
      if (!this.products.length) { this.status.textContent = this.text('missing_products'); return; }
      this.productSelect.replaceChildren();
      this.gelSelect.replaceChildren();
      this.wickProductSelect.replaceChildren();
      for (const product of this.products) this.productSelect.add(new Option(product.label || product.title,product.handle));
      const initial = this.products.find(product=>product.handle === this.dataset.productHandle) || this.products[0];
      this.productSelect.value = initial.handle;
      const preset = initial.variants.find(variant=>String(variant.id) === this.dataset.variantId) || initial.variants.find(variant=>variant.available) || initial.variants[0];
      const wi = optionIndex(initial,'Gramaj'), ai = optionIndex(initial,'Koku');
      this.preferredAroma = ai >= 0 ? preset.options[ai] : '';
      this.refreshOptions(preset.options[wi]);
      for (const variant of this.catalog.gel?.variants || []) {
        const grams = packageGrams(variant);
        const label = variant.title === 'Default Title' ? (grams > 0 ? this.number.format(grams / 1000) + ' kg' : this.catalog.gel.title) : variant.title;
        this.gelSelect.add(new Option(label + ' · ' + this.money.format(variant.priceMinor / 100),variant.id));
      }
      const initialGel = this.catalog.gel?.variants.find(variant=>variant.available) || this.catalog.gel?.variants[0];
      if (initialGel) this.gelSelect.value = String(initialGel.id);
      else this.query('[data-fpw-gel-enabled]').disabled = true;
      this.image('[data-fpw-gel-image]',this.catalog.gel?.image,this.catalog.gel?.title);
      if (this.catalog.gel?.url) this.query('[data-fpw-gel-link]').href = this.catalog.gel.url;
      for(const product of this.catalog.wicks || [])this.wickProductSelect.add(new Option(product.title,product.handle));
      const preferredWick=(this.catalog.wicks || []).find(product=>product.handle==='parafinlenmis-pamuk-mum-fitili-1-4-mm') || this.catalog.wicks?.[0];
      if(preferredWick)this.wickProductSelect.value=preferredWick.handle;
      this.refreshWickVariants();
      const unwaxedList=this.query('[data-fpw-unwaxed]');
      for(const product of this.catalog.unwaxed || []) {
        const link=document.createElement('a');link.href=product.url;link.textContent=product.title;unwaxedList.append(link);
      }
      this.addEventListener('click',event=>event.stopPropagation(),{signal});
      this.addEventListener('change',event=>{
        event.stopPropagation();
        if (event.target === this.productSelect) this.refreshOptions(this.weightSelect.value);
        else if (event.target === this.weightSelect) this.refreshAromas();
        else if (event.target === this.aromaSelect) this.preferredAroma = this.aromaSelect.value;
        else if(event.target === this.wickProductSelect)this.refreshWickVariants();
        this.update();
      },{signal});
      this.addEventListener('input',event=>{event.stopPropagation();this.update();},{signal});
      this.query('[data-fpw-form]').addEventListener('submit',event=>{event.preventDefault();event.stopPropagation();this.submit();},{signal});
      const owner = this.closest('product-info');
      if (owner && this.dataset.context === 'product') {
        const sync = event=>{
          if (this.contains(event?.target)) return;
          const id = owner.querySelector('input[name="id"]')?.value;
          const product = this.products.find(p=>p.variants.some(v=>String(v.id) === String(id)));
          const variant = product?.variants.find(v=>String(v.id) === String(id));
          if (!variant) return;
          this.productSelect.value = product.handle;
          const ai = optionIndex(product,'Koku');
          if (ai >= 0) this.preferredAroma = variant.options[ai];
          this.refreshOptions(variant.options[optionIndex(product,'Gramaj')]);this.update();
        };
        owner.addEventListener('change',sync,{signal});
        for (const name of ['variant:update','product:updated']) document.addEventListener(name,sync,{signal});
      }
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); this.mounted = false; }
    text(key) { return this.copy?.[key] || key; }
    product() { return this.products.find(product=>product.handle === this.productSelect.value); }
    variant() {
      const product = this.product(), wi = optionIndex(product,'Gramaj'), ai = optionIndex(product,'Koku');
      return product?.variants.find(variant=>variant.options[wi] === this.weightSelect.value && (ai < 0 || variant.options[ai] === this.aromaSelect.value));
    }
    refreshOptions(weight) {
      const product = this.product(), wi = optionIndex(product,'Gramaj');
      this.weightSelect.replaceChildren();
      for (const value of [...new Set(product.variants.map(variant=>variant.options[wi]))]) this.weightSelect.add(new Option(value,value));
      if ([...this.weightSelect.options].some(option=>option.value === weight)) this.weightSelect.value = weight;
      this.refreshAromas();
      this.image('[data-fpw-image]',product.image,product.title);
    }
    refreshAromas() {
      const product = this.product(), wi = optionIndex(product,'Gramaj'), ai = optionIndex(product,'Koku');
      this.aromaSelect.replaceChildren();
      this.query('[data-fpw-aroma-wrap]').hidden = ai < 0;
      this.aromaSelect.required = ai >= 0;
      if (ai < 0) return;
      const variants = product.variants.filter(variant=>variant.options[wi] === this.weightSelect.value);
      for (const value of [...new Set(variants.map(variant=>variant.options[ai]))]) this.aromaSelect.add(new Option(value,value));
      if ([...this.aromaSelect.options].some(option=>option.value === this.preferredAroma)) this.aromaSelect.value = this.preferredAroma;
      this.preferredAroma = this.aromaSelect.value;
    }
    image(selector,url,title) {
      const image = this.query(selector);image.hidden = !url;
      if (url) { image.src = url; image.alt = title || ''; }
    }
    refreshWickVariants() {
      this.wickVariantSelect.replaceChildren();
      const product=this.catalog.wicks?.find(product=>product.handle===this.wickProductSelect.value);
      for(const variant of product?.variants || [])this.wickVariantSelect.add(new Option(variant.title + ' · ' + this.money.format(variant.priceMinor/100),variant.id));
      const variant=product?.variants.find(variant=>variant.available) || product?.variants[0];
      if(variant)this.wickVariantSelect.value=String(variant.id);
    }
    calculate() {
      const wickProduct=this.catalog.wicks?.find(product=>product.handle===this.wickProductSelect.value);
      return plan({count:Number(this.query('[data-fpw-count]').value),gramsPerCandle:Number(this.query('[data-fpw-grams]').value),variant:this.variant(),scented:this.product().scented,gelEnabled:this.query('[data-fpw-gel-enabled]').checked,gelVariant:this.catalog.gel?.variants.find(variant=>String(variant.id) === this.gelSelect.value),gelGramsPerCandle:Number(this.query('[data-fpw-gel-grams]').value),wicksPerCandle:Number(this.query('[data-fpw-wicks-per-candle]').value),wickMode:this.query('[data-fpw-wick-mode]').value,buyWicks:this.query('[data-fpw-buy-wicks]').checked,wickProduct,wickVariant:wickProduct?.variants.find(variant=>String(variant.id)===this.wickVariantSelect.value)});
    }
    update(keepStatus=false) {
      const variant = this.variant(), product = this.product(), result = this.calculate();
      this.result = result;
      if (!keepStatus) this.status.textContent = result.error ? this.text(result.error) : '';
      this.addButton.disabled = this.busy || Boolean(result.error);
      this.query('[data-fpw-gel-grams]').disabled = !this.query('[data-fpw-gel-enabled]').checked;
      const tabbed=this.query('[data-fpw-wick-mode]').value==='tabbed';
      this.query('[data-fpw-tabbed-options]').hidden=!tabbed;
      this.query('[data-fpw-buy-wicks]').disabled=!tabbed;
      const unit = unitMinor(variant);
      this.query('[data-fpw-unit]').textContent = unit == null ? '' : this.money.format(unit / 100) + ' / kg';
      const grams = packageGrams(variant);
      this.query('[data-fpw-gift]').textContent = this.text(grams === 1000 ? (product.scented ? 'gift_scented' : 'gift_plain') : 'gift_bulk');
      this.query('[data-fpw-product-link]').href = variant?.url || product.url;
      const output = this.query('[data-fpw-result]');output.replaceChildren();
      if (result.error) return;
      const row = (label,value,total=false)=>{
        const node = document.createElement('p');if (total) node.className = 'fpw-total';
        const title = document.createElement('span'), amount = document.createElement('strong');title.textContent=label;amount.textContent=value;node.append(title,amount);output.append(node);
      };
      row(this.text('need'),this.number.format(result.pearl.needG) + ' g');
      row(this.text('package'),result.pearl.quantity + ' × ' + this.weightSelect.value + ' · ' + this.money.format(result.pearl.costMinor / 100));
      row(this.text('leftover'),this.number.format(result.pearl.leftoverG) + ' g');
      row(this.text('wick_required'),this.number.format(result.wick.required)+' '+this.text('pieces'));
      row(this.text('wick_gift'),this.number.format(result.wick.gift)+' '+this.text('pieces'));
      if(result.wick.id)row(this.text('wick_cost'),result.wick.quantity+' × '+result.wick.packCount+' '+this.text('pieces')+' · '+this.money.format(result.wick.costMinor/100));
      else row(this.text('wick_own'),this.number.format(result.wick.ownNeeded)+' '+this.text('pieces'));
      if (result.gel) { row(this.text('gel_need'),this.number.format(result.gel.needG) + ' g');row(this.text('gel_title'),result.gel.quantity + ' × ' + this.gelSelect.selectedOptions[0].textContent); }
      row(this.text('total'),this.money.format(result.totalMinor / 100),true);
    }
    async submit() {
      if (this.busy || !this.query('[data-fpw-form]').reportValidity()) return;
      const result = this.calculate();if (result.error) { this.update();return; }
      this.busy = true;this.update(true);this.status.textContent=this.text('adding');
      try {
        if (typeof window.FabrikoneSafeCart?.add !== 'function') throw new Error(this.text('cart_missing'));
        await window.FabrikoneSafeCart.add(result.items);
        this.status.textContent=this.text('added');
      } catch (error) { this.status.textContent=error.message || this.text('failed'); }
      finally { this.busy=false;this.update(true); }
    }
  });
})();
